import type { JobUserXingApplicationStatus } from '@xing-com/crate-common-graphql-types';
import type { ApplicationType } from '@xing-com/crate-jobs-helpers';
import {
  useApplicationTypeHelpers,
  useApplicationStatusHelpers,
} from '@xing-com/crate-jobs-hooks';
import { useLoginState } from '@xing-com/hub';

type UseMainActionsResult = {
  showApplyButton: boolean;
};

type Application = {
  __typename?: ApplicationType;
  type?: ApplicationType;
  applyUrl?: string | null;
  applyEmail?: string | null;
  contactUserId?: string | null;
  submittedAt?: string;
  createdAt?: string;
  status?: JobUserXingApplicationStatus;
};

type Job = {
  slug: string;
  redirectsToThirdPartyUrl: boolean;
  paid: boolean;
  application?: Application | null;
};

type Props = {
  job: Job | null;
};

export const useShowApplyButton = ({ job }: Props): UseMainActionsResult => {
  const loginState = useLoginState();
  const { application } = job || {};
  const applicationType = application?.type || application?.__typename;
  const { appliedAt } = useApplicationStatusHelpers({
    slug: job?.slug,
    submittedAt: application?.submittedAt || application?.createdAt,
  });
  const { isUrlApplication, isEmailApplication, isMessageApplication } =
    useApplicationTypeHelpers(applicationType);

  const isApplyUrlMissing = isUrlApplication && !application?.applyUrl;
  const isApplyEmailMissing =
    isEmailApplication &&
    loginState !== 'LOGGED_OUT' &&
    !application?.applyEmail; // applyEmail is restricted to LI and SLI users on x1
  const isContactUserIdMissing =
    isMessageApplication &&
    loginState !== 'LOGGED_OUT' &&
    !application?.contactUserId; // contactUserId is restricted to LI and SLI users on x1

  const isValidApplyData = !(
    isApplyUrlMissing ||
    isApplyEmailMissing ||
    isContactUserIdMissing
  );

  const hasUserApplied = !!appliedAt;
  const showApplyButton =
    !!applicationType &&
    !!job &&
    job.paid &&
    !hasUserApplied &&
    isValidApplyData &&
    !job.redirectsToThirdPartyUrl;

  return {
    showApplyButton,
  };
};
